import { useState, useEffect } from "react";
import {
    Modal,
    Button,
    Checkbox,
    Stack,
    Group,
    Text,
    Alert,
    Divider,
    TextInput,
    Pagination,
} from "@mantine/core";
import { FaInfoCircle, FaSearch } from "react-icons/fa";
import ModalButtons from "components/ui/Modal/ModalButtons";
import { LicenseManager, LicenseManagerModalProps } from "types/module/License";
import { getManagerEmail } from "helpers";



const LicenseManagerModal: React.FC<LicenseManagerModalProps> = ({
    opened,
    onClose,
    availableManagers,
    selectedPools,
    onAddManagers,
    isLoading = false,
}) => {
    const [selectedManagers, setSelectedManagers] = useState<LicenseManager[]>([]);
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);

    // Filter managers based on search query only
    // For multi-pool assignments, show all managers regardless of current assignments
    // Exclude admins/super users as they are automatically assigned
    // Exclude managers without valid email (email is now required for LM operations as externalId in PAM)
    // The backend will handle duplicate assignments gracefully
    const filteredManagers = availableManagers.filter(manager => {
        const matchesSearch = manager.name.toLowerCase().includes(searchQuery.toLowerCase());
        const isNotAdmin = !manager.isAdmin;
        const hasValidEmail = getManagerEmail(manager) !== '';
        return matchesSearch && isNotAdmin && hasValidEmail;
    });

    // Pagination
    const ITEMS_PER_PAGE = 5;
    const totalPages = Math.ceil(filteredManagers.length / ITEMS_PER_PAGE);
    const paginatedManagers = filteredManagers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => {
        // Reset selections and search when modal opens
        if (opened) {
            setSelectedManagers([]);
            setAllSelected(false);
            setSearchQuery("");
            setCurrentPage(1);
        }
    }, [opened]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]); const handleManagerToggle = (manager: LicenseManager) => {
        const managerEmail = getManagerEmail(manager);
        setSelectedManagers(prev => {
            // Use email as the primary key for LM operations
            const isSelected = prev.some(m => getManagerEmail(m) === managerEmail);
            if (isSelected) {
                return prev.filter(m => getManagerEmail(m) !== managerEmail);
            } else {
                return [...prev, manager];
            }
        });
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedManagers([]);
            setAllSelected(false);
        } else {
            setSelectedManagers([...filteredManagers]);
            setAllSelected(true);
        }
    };

    useEffect(() => {
        setAllSelected(selectedManagers.length === filteredManagers.length && filteredManagers.length > 0);
    }, [selectedManagers, filteredManagers]); const handleAdd = () => {
        onAddManagers(selectedManagers);
        // Don't close immediately - let the parent close on success
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title="Assign License Managers"
            size="md"
            centered
        >
            <Stack gap="md">
                <Alert icon={<FaInfoCircle />} color="blue">
                    <Text size="sm">
                        You&apos;re assigning one or more license managers to {selectedPools.length} selected pools. Any license manager already linked to one of those pools will stay assigned.
                    </Text>
                </Alert>

                <Group justify="space-between">
                    <Text fw={500}>Available License Managers:</Text>
                    <Group gap="xs">
                        <Button
                            variant="light"
                            size="xs"
                            onClick={handleSelectAll}
                        >
                            {allSelected ? "Deselect All" : "Select All"}
                        </Button>
                    </Group>
                </Group>

                <TextInput
                    placeholder="Search license managers..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.currentTarget.value)}
                    leftSection={<FaSearch />}
                    size="sm"
                />

                <Divider />

                <Stack gap="xs" mah={300} style={{ overflowY: 'auto' }}>
                    {paginatedManagers.map((manager) => (
                        <Checkbox
                            key={getManagerEmail(manager)}
                            label={`${manager.name}`}
                            checked={selectedManagers.some(m => getManagerEmail(m) === getManagerEmail(manager))}
                            onChange={() => handleManagerToggle(manager)}
                            color="gray"
                            size="sm"
                        />
                    ))}
                </Stack>

                {totalPages > 1 && (
                    <Group justify="center">
                        <Pagination
                            size="sm"
                            total={totalPages}
                            value={currentPage}
                            onChange={setCurrentPage}
                            withEdges={false}
                        />
                    </Group>
                )}

                {filteredManagers.length === 0 && searchQuery && (
                    <Alert color="yellow">
                        <Text size="sm">No license managers found matching &ldquo;{searchQuery}&rdquo;.</Text>
                    </Alert>
                )}

                {availableManagers.length === 0 && (
                    <Alert color="yellow">
                        <Text size="sm">No available license managers to assign.</Text>
                    </Alert>
                )}

                <ModalButtons>
                    <Button variant="default" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        color="orange"
                        onClick={handleAdd}
                        disabled={selectedManagers.length === 0}
                        loading={isLoading}
                    >
                        Assign Selected ({selectedManagers.length})
                    </Button>
                </ModalButtons>
            </Stack>
        </Modal>
    );
};

export default LicenseManagerModal;
